import React, { useState, useEffect } from 'react';
import styles from './app.css';
import STLViewerExample from './components/STLViewer/STLViewerExample';
import STLViewer from './components/STLViewer';

function App() {
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [showToyOrderModal, setShowToyOrderModal] = useState(false);
  const [showOrderConfirmModal, setShowOrderConfirmModal] = useState(false);
  const [registerForm, setRegisterForm] = useState({
    name: '',
    email: '',
    password: '',
    password2: '',
    subscribe: false
  });
  const [orderForm, setOrderForm] = useState({
    name: '',
    email: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'US',
    toyType: 'creature',
    quantity: 1
  });
  const [registerError, setRegisterError] = useState('');
  const [registerSuccess, setRegisterSuccess] = useState(false);
  const [orderError, setOrderError] = useState('');
  const [orderSuccess, setOrderSuccess] = useState(false);
  const [orderLoading, setOrderLoading] = useState(false);

  // Check for order success/cancel URL parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('order') === 'success') {
      setShowOrderConfirmModal(true);
      // Clean up URL without page reload
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const handleRegisterSubmit = async (e) => {
    e.preventDefault();
    setRegisterError('');

    // Basic validation
    if (!registerForm.name || !registerForm.email || !registerForm.password || !registerForm.password2) {
      setRegisterError('Please fill in all fields');
      return;
    }

    if (registerForm.password !== registerForm.password2) {
      setRegisterError('Passwords do not match');
      return;
    }

    if (registerForm.password.length < 6) {
      setRegisterError('Password must be at least 6 characters');
      return;
    }

    try {
      const response = await fetch('https://onedevbird.com:6969/users/register', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(registerForm),
      });

      if (response.ok) {
        setRegisterSuccess(true);
        setTimeout(() => {
          setShowRegisterModal(false);
          setRegisterSuccess(false);
          setRegisterForm({
            name: '',
            email: '',
            password: '',
            password2: '',
            subscribe: false
          });
          // Redirect to the game
          window.location.href = '/view';
        }, 2000);
      } else {
        const errorData = await response.text();
        setRegisterError('Registration failed. Email may already be registered.');
      }
    } catch (error) {
      setRegisterError('Network error. Please try again.');
    }
  };

  const handleToyOrderSubmit = async (e) => {
    e.preventDefault();
    setOrderError('');
    setOrderLoading(true);

    // Basic validation
    if (!orderForm.name || !orderForm.email || !orderForm.address || !orderForm.city || !orderForm.state || !orderForm.zipCode) {
      setOrderError('Please fill in all shipping fields');
      setOrderLoading(false);
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(orderForm.email)) {
      setOrderError('Please enter a valid email address');
      setOrderLoading(false);
      return;
    }

    try {
      // Calculate shipping cost based on country
      const toyPrice = 29.99;
      const quantity = orderForm.quantity || 1;
      const subtotal = toyPrice * quantity;
      const shippingCost = orderForm.country === 'US' ? 5.99 : 15.99;
      const totalAmount = Math.round((subtotal + shippingCost) * 100); // Convert to cents

      const orderData = {
        ...orderForm,
        toyPrice,
        shippingCost,
        totalAmount: totalAmount / 100,
        gameSubdomain: 'black-lagoon',
        gameName: 'Black Lagoon'
      };

      console.log('Submitting toy order:', orderData);

      const response = await fetch('https://onedevbird.com:6969/api/toy-orders', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(orderData),
      });

      if (response.ok) {
        const result = await response.json();
        
        // Redirect to Stripe Checkout
        if (result.checkoutUrl) {
          window.location.href = result.checkoutUrl;
        } else {
          setOrderError('Payment setup failed. Please try again.');
        }
      } else {
        const errorData = await response.json();
        setOrderError(errorData.message || 'Order failed. Please try again.');
      }
    } catch (error) {
      setOrderError('Network error. Please check your connection and try again.');
    } finally {
      setOrderLoading(false);
    }
  };

  return <div className="app">
    <h1 className="logo">Black Lagoon</h1>
    <STLViewer
      stlFile="/assets/stl/LagoonGuy.stl"
      autoRotate={true}
      autoRotateAxis="z"
      lightIntensity={4}
      cameraDistance={2}
      initialRotationX={-1.50}
      initialRotationY={0}
      initialRotationZ={0}
    />
    <div className="hero-section">
      <h2>Collectible Creature Toy</h2>
      <p className="hero-text">
        Meet your new underwater companion! This beautifully crafted creature looks stunning on your shelf, 
        but the real magic happens when you tap it to your device. Instantly unlock the full Black Lagoon 
        experience with exclusive features, bonus content, and direct access to your AI creature friend.
      </p>
      
      <div className="features-grid">
        <div className="feature-card">
          <h3>Premium Game Access</h3>
          <p>Unlock all game modes, unlimited level creation, and exclusive creature interactions</p>
        </div>
        <div className="feature-card">
          <h3>AI Companion</h3>
          <p>Chat with your creature using advanced AI - ask questions, get hints, or just have fun conversations</p>
        </div>
        <div className="feature-card">
          <h3>Progress Tracking</h3>
          <p>Your scores, achievements, and custom levels sync across all your devices</p>
        </div>
      </div>
    </div>

    <div className="game-section">
      <h2>Dive Into Black Lagoon</h2>
      <p className="game-description">
        An enchanting underwater puzzle adventure where rhythm meets strategy. Help the creature and his 
        musical frog friends create beautiful melodies while keeping their lagoon home safe from rising toxins.
      </p>

      <div className="game-modes">
        <div className="mode-card arcade">
          <h3>Arcade Mode</h3>
          <ul>
            <li><strong>Rhythm-Based Gameplay:</strong> Bubbles spawn to the beat of underwater melodies</li>
            <li><strong>Color Matching:</strong> Pop connected bubbles of the same color to clear the lagoon</li>
            <li><strong>Frog Rescue:</strong> Free musical frogs trapped in bubbles to unlock new instruments</li>
            <li><strong>Chemical Management:</strong> Keep toxic, nuclear, and plasma levels under control</li>
            <li><strong>Power-Ups:</strong> Use bombs and special items strategically</li>
            <li><strong>Endless Challenge:</strong> Survive as long as possible with increasing difficulty</li>
          </ul>
        </div>

        <div className="mode-card puzzle">
          <h3>Puzzle Mode</h3>
          <ul>
            <li><strong>10 Handcrafted Levels:</strong> From simple patterns to complex challenges</li>
            <li><strong>Time Pressure:</strong> Clear all bubbles before the timer runs out</li>
            <li><strong>Strategic Thinking:</strong> Plan your moves carefully - every bubble counts</li>
            <li><strong>Progressive Difficulty:</strong> Master basic concepts, then face expert challenges</li>
            <li><strong>Perfect Scores:</strong> Compete for the fastest completion times</li>
          </ul>
        </div>
      </div>

      <div className="premium-features">
        <h3>Premium Features (Toy or Account Required)</h3>
        <div className="premium-grid">
          <div className="premium-item">
            <strong>Level Creator:</strong> Design your own challenging bubble layouts and share with friends
          </div>
          <div className="premium-item">
            <strong>AI Creature Chat:</strong> Have real conversations with your underwater friend
          </div>
          <div className="premium-item">
            <strong>Global Leaderboards:</strong> Compete with players worldwide
          </div>
          <div className="premium-item">
            <strong>Achievement System:</strong> Unlock badges for your gaming prowess
          </div>
          <div className="premium-item">
            <strong>Cloud Save:</strong> Your progress syncs across all devices
          </div>
        </div>
      </div>
    </div>

    <div className="cta-section">
      <h2>Start Your Adventure</h2>
      <p>Jump in now with free Arcade and Puzzle modes, or unlock the full experience!</p>
      <div className="cta-buttons">
        <a href="/view" className="cta-button primary">Play Free Now</a>
        <button 
          onClick={() => setShowRegisterModal(true)}
          className="cta-button secondary"
        >
          Create Free Account
        </button>
        <button 
          onClick={() => setShowToyOrderModal(true)}
          className="cta-button secondary"
        >
          Buy A Toy
        </button>
      </div>
    </div>

    {/* Registration Modal */}
    {showRegisterModal && (
      <div className="modal-overlay" onClick={() => setShowRegisterModal(false)}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h2>Create Your Account</h2>
            <button 
              className="modal-close"
              onClick={() => setShowRegisterModal(false)}
            >
              ×
            </button>
          </div>
          
          <div className="modal-info">
            <p>
              You're creating a <strong>One Dead Bird</strong> account that unlocks premium features 
              in Black Lagoon and works with all of our games. One account, endless adventures!
            </p>
          </div>
          
          {registerSuccess ? (
            <div className="success-message">
              <h3>Account Created Successfully!</h3>
              <p>Welcome to Black Lagoon! You can now access all premium features.</p>
              <p><strong>Redirecting you to the game...</strong></p>
            </div>
          ) : (
            <form onSubmit={handleRegisterSubmit} className="register-form">
              {registerError && (
                <div className="error-message">{registerError}</div>
              )}
              
              <div className="form-field">
                <label htmlFor="name">Name</label>
                <input
                  type="text"
                  id="name"
                  value={registerForm.name}
                  onChange={(e) => setRegisterForm({...registerForm, name: e.target.value})}
                  placeholder="Enter your name"
                  required
                />
              </div>
              
              <div className="form-field">
                <label htmlFor="email">Email</label>
                <input
                  type="email"
                  id="email"
                  value={registerForm.email}
                  onChange={(e) => setRegisterForm({...registerForm, email: e.target.value})}
                  placeholder="Enter your email"
                  required
                />
              </div>
              
              <div className="form-field">
                <label htmlFor="password">Password</label>
                <input
                  type="password"
                  id="password"
                  value={registerForm.password}
                  onChange={(e) => setRegisterForm({...registerForm, password: e.target.value})}
                  placeholder="Create password (6+ characters)"
                  required
                />
              </div>
              
              <div className="form-field">
                <label htmlFor="password2">Confirm Password</label>
                <input
                  type="password"
                  id="password2"
                  value={registerForm.password2}
                  onChange={(e) => setRegisterForm({...registerForm, password2: e.target.value})}
                  placeholder="Confirm your password"
                  required
                />
              </div>
              
              <div className="form-field checkbox-field">
                <input
                  type="checkbox"
                  id="subscribe"
                  checked={registerForm.subscribe}
                  onChange={(e) => setRegisterForm({...registerForm, subscribe: e.target.checked})}
                />
                <label htmlFor="subscribe">Subscribe for game updates and news</label>
              </div>
              
              <button type="submit" className="cta-button primary full-width">
                Create Account
              </button>
            </form>
          )}
        </div>
      </div>
    )}

    {/* Toy Order Modal */}
    {showToyOrderModal && (
      <div className="modal-overlay" onClick={() => setShowToyOrderModal(false)}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h2>Order Your Black Lagoon Toy</h2>
            <button 
              className="modal-close"
              onClick={() => setShowToyOrderModal(false)}
            >
              ×
            </button>
          </div>
          
          <div className="modal-info">
            <p>
              Get your own <strong>Black Lagoon Creature</strong> collectible! This premium toy unlocks 
              all game features and looks amazing on your shelf. Includes NFC technology for instant game access.
            </p>
          </div>
          
          {orderSuccess ? (
            <div className="success-message">
              <h3>Order Placed Successfully!</h3>
              <p>Thank you for your order! You'll receive a confirmation email shortly.</p>
              <p><strong>Your toy will ship within 3-5 business days.</strong></p>
            </div>
          ) : (
            <form onSubmit={handleToyOrderSubmit} className="register-form">
              {orderError && (
                <div className="error-message">{orderError}</div>
              )}
              
              <div className="form-field">
                <label htmlFor="order-quantity">Quantity</label>
                <select
                  id="order-quantity"
                  value={orderForm.quantity}
                  onChange={(e) => setOrderForm({...orderForm, quantity: parseInt(e.target.value)})}
                  required
                >
                  <option value="1">1 Toy</option>
                  <option value="2">2 Toys</option>
                  <option value="3">3 Toys</option>
                  <option value="4">4 Toys</option>
                  <option value="5">5 Toys</option>
                </select>
              </div>
              
              <div className="order-summary">
                <h3>Order Summary</h3>
                <div className="price-line">
                  <span>Black Lagoon Creature Toy</span>
                  <span>${(29.99 * orderForm.quantity).toFixed(2)}</span>
                </div>
                <div className="price-line">
                  <span>Shipping ({orderForm.country === 'US' ? 'US' : 'International'})</span>
                  <span>${orderForm.country === 'US' ? '5.99' : '15.99'}</span>
                </div>
                <div className="price-line total">
                  <span><strong>Total</strong></span>
                  <span><strong>${((29.99 * orderForm.quantity) + (orderForm.country === 'US' ? 5.99 : 15.99)).toFixed(2)}</strong></span>
                </div>
              </div>
              
              <h3>Contact Information</h3>
              <div className="form-row">
                <div className="form-field">
                  <label htmlFor="order-name">Full Name</label>
                  <input
                    type="text"
                    id="order-name"
                    value={orderForm.name}
                    onChange={(e) => setOrderForm({...orderForm, name: e.target.value})}
                    placeholder="Enter your full name"
                    required
                  />
                </div>
                
                <div className="form-field">
                  <label htmlFor="order-email">Email Address</label>
                  <input
                    type="email"
                    id="order-email"
                    value={orderForm.email}
                    onChange={(e) => setOrderForm({...orderForm, email: e.target.value})}
                    placeholder="Enter your email"
                    required
                  />
                </div>
              </div>
              
              <h3>Shipping Address</h3>
              <div className="form-field">
                <label htmlFor="order-address">Street Address</label>
                <input
                  type="text"
                  id="order-address"
                  value={orderForm.address}
                  onChange={(e) => setOrderForm({...orderForm, address: e.target.value})}
                  placeholder="Enter your street address"
                  required
                />
              </div>
              
              <div className="form-row three-col">
                <div className="form-field">
                  <label htmlFor="order-city">City</label>
                  <input
                    type="text"
                    id="order-city"
                    value={orderForm.city}
                    onChange={(e) => setOrderForm({...orderForm, city: e.target.value})}
                    placeholder="City"
                    required
                  />
                </div>
                
                <div className="form-field">
                  <label htmlFor="order-state">State/Province</label>
                  <input
                    type="text"
                    id="order-state"
                    value={orderForm.state}
                    onChange={(e) => setOrderForm({...orderForm, state: e.target.value})}
                    placeholder="State/Province"
                    required
                  />
                </div>
                
                <div className="form-field">
                  <label htmlFor="order-zip">ZIP/Postal Code</label>
                  <input
                    type="text"
                    id="order-zip"
                    value={orderForm.zipCode}
                    onChange={(e) => setOrderForm({...orderForm, zipCode: e.target.value})}
                    placeholder="ZIP Code"
                    required
                  />
                </div>
              </div>
              
              <div className="form-field">
                <label htmlFor="order-country">Country</label>
                <select
                  id="order-country"
                  value={orderForm.country}
                  onChange={(e) => setOrderForm({...orderForm, country: e.target.value})}
                  required
                >
                  <option value="US">United States</option>
                  <option value="CA">Canada</option>
                  <option value="GB">United Kingdom</option>
                  <option value="AU">Australia</option>
                  <option value="DE">Germany</option>
                  <option value="FR">France</option>
                  <option value="JP">Japan</option>
                  <option value="OTHER">Other</option>
                </select>
              </div>
              
              <button type="submit" className="cta-button primary full-width" disabled={orderLoading}>
                {orderLoading ? 'Processing Order...' : `Place Order - $${(29.99 + (orderForm.country === 'US' ? 5.99 : 15.99)).toFixed(2)}`}
              </button>
              
              <p className="payment-info">
                <small>Secure payment processing powered by Stripe. You'll be redirected to complete payment after placing your order.</small>
              </p>
            </form>
          )}
        </div>
      </div>
    )}

    {/* Order Confirmation Modal */}
    {showOrderConfirmModal && (
      <div className="modal-overlay" onClick={() => setShowOrderConfirmModal(false)}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h2>🎉 Order Confirmed!</h2>
            <button 
              className="modal-close"
              onClick={() => setShowOrderConfirmModal(false)}
            >
              ×
            </button>
          </div>
          
          <div className="success-message">
            <h3>Your Black Lagoon Creature{orderForm.quantity > 1 ? 's are' : ' is'} on the way!</h3>
            <p>Thank you for your order! You'll receive a confirmation email shortly with tracking information.</p>
            <p><strong>Estimated delivery: 3-5 business days</strong></p>
          </div>
          
          <div className="confirmation-ctas">
            <h3>While you wait...</h3>
            <p>Get the most out of your Black Lagoon experience:</p>
            
            <div className="cta-buttons">
              <button 
                onClick={() => {
                  setShowOrderConfirmModal(false);
                  setShowRegisterModal(true);
                }}
                className="cta-button primary"
              >
                Create Free Account
              </button>
              <a href="/view" className="cta-button secondary">
                Play Now
              </a>
            </div>
            
            <div className="toy-info">
              <h4>About your toy:</h4>
              <p>Your Black Lagoon Creature includes NFC technology that instantly connects to the game. Simply tap it on your phone or scan the QR code to unlock premium features and sync your progress!</p>
            </div>
          </div>
        </div>
      </div>
    )}
  </div>;
}

export default App;
